// AI Duplicate Entry Detection Engine
import { Voucher, VoucherLine, VoucherType } from "@/lib/accounting/types";

export interface DuplicateMatch {
  matchedVoucher: Voucher;
  matchType: "exact_invoice" | "amount_party_date" | "amount_date" | "similar_narration";
  confidence: number;
  reason: string;
}

/**
 * Detects potential duplicate entries by comparing new entry data against existing vouchers.
 * Checks: invoice number, amount+party+date, amount+date, and narration similarity.
 */
export function detectDuplicates(
  existingVouchers: Voucher[],
  opts: {
    type?: VoucherType;
    invoiceNumber?: string;
    partyLedgerId?: string;
    partyName?: string;
    totalAmount?: number;
    narration?: string;
    date?: string;
  }
): DuplicateMatch[] {
  const matches: DuplicateMatch[] = [];
  const active = existingVouchers.filter(v => v.status !== "Cancelled");

  // 1. Exact invoice number match (highest confidence)
  if (opts.invoiceNumber && opts.invoiceNumber.trim()) {
    const invNum = opts.invoiceNumber.trim().toLowerCase();
    for (const v of active) {
      if (v.invoiceNumber && v.invoiceNumber.trim().toLowerCase() === invNum) {
        matches.push({
          matchedVoucher: v,
          matchType: "exact_invoice",
          confidence: 0.98,
          reason: `Invoice #${opts.invoiceNumber} already exists as ${v.voucherNumber} (${v.type}, ₹${v.totalDebit.toLocaleString("en-IN")})`,
        });
      }
    }
  }

  // 2. Same amount + same party + same/close date
  if (opts.totalAmount && opts.totalAmount > 0 && opts.partyLedgerId) {
    for (const v of active) {
      if (
        v.partyLedgerId === opts.partyLedgerId &&
        Math.abs(v.totalDebit - opts.totalAmount) < 0.01
      ) {
        // Check if already matched by invoice
        if (matches.some(m => m.matchedVoucher.id === v.id)) continue;

        const daysDiff = opts.date ? Math.abs(daysBetween(v.date, opts.date)) : 999;
        if (daysDiff <= 7) {
          matches.push({
            matchedVoucher: v,
            matchType: "amount_party_date",
            confidence: daysDiff === 0 ? 0.92 : daysDiff <= 3 ? 0.82 : 0.7,
            reason: `Same amount (₹${opts.totalAmount.toLocaleString("en-IN")}) to same party "${v.partyName || "Unknown"}" on ${v.date} — ${v.voucherNumber}`,
          });
        }
      }
    }
  }

  // 3. Same amount + same date (no party match)
  if (opts.totalAmount && opts.totalAmount > 0 && opts.date) {
    for (const v of active) {
      if (
        Math.abs(v.totalDebit - opts.totalAmount) < 0.01 &&
        v.date === opts.date &&
        (!opts.type || v.type === opts.type)
      ) {
        if (matches.some(m => m.matchedVoucher.id === v.id)) continue;
        matches.push({
          matchedVoucher: v,
          matchType: "amount_date",
          confidence: 0.6,
          reason: `Same amount (₹${opts.totalAmount.toLocaleString("en-IN")}) on same date — ${v.voucherNumber} (${v.type})`,
        });
      }
    }
  }

  // 4. Similar narration (fuzzy)
  if (opts.narration && opts.narration.length >= 10) {
    const narLower = opts.narration.toLowerCase().trim();
    for (const v of active) {
      if (!v.narration || v.narration.length < 10) continue;
      if (matches.some(m => m.matchedVoucher.id === v.id)) continue;

      const similarity = jaccardSimilarity(narLower, v.narration.toLowerCase().trim());
      if (similarity > 0.7) {
        matches.push({
          matchedVoucher: v,
          matchType: "similar_narration",
          confidence: Math.min(0.75, similarity),
          reason: `Similar description to ${v.voucherNumber}: "${v.narration.slice(0, 60)}..."`,
        });
      }
    }
  }

  return matches.sort((a, b) => b.confidence - a.confidence).slice(0, 5);
}

function daysBetween(d1: string, d2: string): number {
  const a = new Date(d1).getTime();
  const b = new Date(d2).getTime();
  return Math.round(Math.abs(a - b) / 86400000);
}

function jaccardSimilarity(a: string, b: string): number {
  const setA = new Set(a.split(/\s+/).filter(w => w.length > 2));
  const setB = new Set(b.split(/\s+/).filter(w => w.length > 2));
  if (setA.size === 0 || setB.size === 0) return 0;
  let intersection = 0;
  for (const w of setA) {
    if (setB.has(w)) intersection++;
  }
  return intersection / (setA.size + setB.size - intersection);
}
